<template>
  <div>
    <header class="">
      <div class="row pt-3 align-items-baseline g-2">
        <div class="col col-auto h5 pe-3">
          {{ this.$t('PartSearch.assignPart') }}
        </div>
        <div class="col pe-3">
          <div
            class="progress"
            role="progressbar"
            v-if="wizard.active"
            :aria-valuenow="wizard.progress + 1"
            aria-valuemin="0"
            :aria-valuemax="wizard.partGroups.length"
          >
            <div
              class="progress-bar overflow-visible"
              :style="{width: `${100*(wizard.progress + 1)/wizard.partGroups.length}%`}"
            >
              {{
                $t('PartSearch.progress', {
                  current: wizard.progress + 1,
                  total: wizard.partGroups.length
                })
              }}
            </div>
          </div>
        </div>
        <div class="col col-auto">
          <button
            class="btn btn-outline-secondary"
            @click="excludePart"
          >
            {{ $t('PartSearch.exclude') }}
          </button>
        </div>
        <div class="col col-auto" v-if="wizard.active">
          <button
            class="btn btn-outline-secondary"
            @click="finishOrProgressWizard"
          >
            {{ $t('PartSearch.skipForNow') }}
          </button>
        </div>
        <div class="col col-auto">
          <button
            class="btn btn-outline-secondary"
            @click="assignStore.finishAssignment"
          >
            {{ wizard.active ? $t('PartSearch.stopAssign') : $t('PartSearch.cancel') }}
          </button>
        </div>
      </div>
    </header>

    <div class="row my-3">
      <div class="col">
        <div class="card">
          <div class="card-body row">
            <div class="col-auto me-1">
              <span class="text-secondary">{{ $t('PartSearch.name') }}</span><br>
              {{ partNames }}
            </div>
            <div class="col-auto me-1">
              <span class="text-secondary">{{ $t('PartSearch.comment') }}</span><br>
              {{ partRepresentative.comment }}
            </div>
            <div class="col-auto me-1">
              <span class="text-secondary">{{ $t('PartSearch.edaMpn') }}</span><br>
              {{ partRepresentative.edaMpn || '-' }}
            </div>
            <div class="col-auto me-1">
              <span class="text-secondary">{{ $t('PartSearch.footprint') }}</span><br>
              {{ partRepresentative.normalizedFootprint || partRepresentative.footprint }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row my-3">
      <p class="col-12" v-html="this.$t('PartSearch.instructions')" />

      <div class="col-12 input-group">
        <span class="input-group-text">
          <i class="fas fa-magnifying-glass" />
        </span>
        <input
          class="form-control"
          type="text"
          :placeholder="this.$t('PartSearch.placeholder')"
          ref="searchInput"
          v-debounce:1s.fireonempty.lock="search"
        >
      </div>
    </div>

    <div class="row my-3 g-3">
      <SmartMatchPackageHint
        class="col-12"
        v-if="smartMatchPackageHint"
        :description="result.smartMatch.description"
        :supported-packages="result.smartMatch.supportedPackages"
        @add-package="addPackage"
      />

      <SmartMatch
        class="col-12"
        v-if="showSmartMatchConfirmation"
        :description="result.smartMatch.description"
        @confirm="assignSmartMatch"
        @decline="runForceSearch"
      />

      <template v-else-if="searchTerm?.length > 0">
        <PhysicalPartList
          class="col-12"
          :parts="result.matchingParts"
          @select-part="assignPart"
          :loading="assigning"
        >
          <template #empty>
            <div class="p-2 text-center">
              <span v-if="assignStore.error">
                {{ $t('PartSearch.error') }}
              </span>
              <template v-else-if="searching">
                <BeatLoader class="my-3" />

                {{
                  distributorSearch ?
                    $t('PartSearch.runningDistributorSearch') :
                    $t('PartSearch.searchingForParts')
                }}
              </template>
              <template v-else-if="!distributorSearch">
                <p>{{ $t('PartSearch.noPartsFound') }}</p>
                <button
                  class="btn btn-primary m-0 mt-2 col-sm-12 col-lg-4 offset-lg-4"
                  @click="runDistributorSearch"
                >
                  {{ $t('PartSearch.runInDepthSearch') }}
                </button>
              </template>
              <div v-else v-html="$t('PartSearch.contactPm')" />
            </div>
          </template>
        </PhysicalPartList>

        <template
          v-if="!searching && !assigning && result.otherParts?.length > 0"
        >
          <h6 class="col-12">
            {{ $t('PartSearch.otherParts') }}
          </h6>

          <PhysicalPartList
            class="col-12"
            :parts="result.otherParts"
            @select-part="assignPart"
          />
        </template>
        <div class="col-12 text-end" v-if="result.matchingParts?.length > 0">
          {{ $t('PartSearch.notFoundWhatYouWhereLookingFor') }}
          <button
            class="btn btn-primary align-right m-0 ms-1"
            :class="{ primary: !distributorSearch, secondary: distributorSearch }"
            :disabled="distributorSearch"
            @click="runDistributorSearch"
          >
            {{ $t('PartSearch.runInDepthSearch') }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import messages from 'locales/PartSearch';
import { vueDebounce } from 'vue-debounce';
import { useBomStore, useAssignStore } from 'store/bom';
import BeatLoader from 'components/shared/BeatLoader.vue';
import PhysicalPartList from './PhysicalPartList.vue';
import SmartMatch from './SmartMatch.vue';
import SmartMatchPackageHint from './SmartMatchPackageHint.vue';

export default {
  name: 'PartAssignment',
  components: {
    BeatLoader,
    SmartMatch,
    SmartMatchPackageHint,
    PhysicalPartList,
  },
  setup() {
    const assignStore = useAssignStore();
    const bomStore = useBomStore();
    return { assignStore, bomStore };
  },
  data() {
    return {
      searchTerm: '',
      forceSearch: false,
      distributorSearch: false,
      assigning: false,
    };
  },
  computed: {
    wizard() {
      return this.assignStore.wizard;
    },
    toBeAssigned() {
      return this.assignStore.toBeAssignedParts;
    },
    result() {
      return this.assignStore.result;
    },
    searching() {
      return this.assignStore.searching;
    },
    partNames() {
      return this.toBeAssigned.map((p) => p.name).join(', ');
    },
    partRepresentative() {
      return this.toBeAssigned[0];
    },
    smartMatchAvailable() {
      return this.result.smartMatch && !this.forceSearch && !this.searching && !this.distributorSearch;
    },
    showSmartMatchConfirmation() {
      return this.smartMatchAvailable && !this.result.smartMatch.missingPackage;
    },
    smartMatchPackageHint() {
      return this.smartMatchAvailable && this.result.smartMatch.missingPackage;
    },
  },
  methods: {
    async search(searchTerm) {
      this.searchTerm = searchTerm;
      await this.assignStore.searchParts(
        this.searchTerm,
        this.partRepresentative.name,
        this.forceSearch,
        this.distributorSearch,
        this.partRepresentative.normalizedFootprint,
      );
    },
    // Complete an incomplete Smart Match by appending the package the customer picked
    async addPackage(supportedPackage) {
      const searchTerm = `${this.searchTerm} ${supportedPackage}`;
      this.$refs.searchInput.value = searchTerm;
      await this.search(searchTerm);
    },
    async runDistributorSearch() {
      this.distributorSearch = true;
      await this.search(this.searchTerm);
    },
    async runForceSearch() {
      this.forceSearch = true;
      await this.search(this.searchTerm);
      this.forceSearch = false;
    },
    async assignSmartMatch() {
      return this.updateParts({
        assignment: {
          parameters: this.result.smartMatch.parameters,
          search_term: this.searchTerm,
        },
      });
    },
    async assignPart(part) {
      return this.updateParts({
        assignment: {
          sku: part.sku,
          search_term: this.searchTerm,
        },
      });
    },
    async excludePart() {
      return this.updateParts({ excluded: true });
    },
    async updateParts(payload) {
      this.error = false;
      this.assigning = true;
      try {
        await this.bomStore.requestPartUpdate(() => this.assignStore.helper.bulkUpdate(this.toBeAssigned, payload));
        await this.finishOrProgressWizard();
      } catch {
        this.error = true;
      }
      this.assigning = false;
    },
    async finishOrProgressWizard() {
      if (this.wizard.active) {
        this.assignStore.progressWizard();
        await this.initializeSearch();
      } else {
        this.assignStore.finishAssignment();
      }
    },
    async initializeSearch() {
      this.forceSearch = false;
      this.distributorSearch = false;
      const part = this.partRepresentative;
      const initialSearchTerm = part.edaMpn || part.comment || part.description;
      this.$refs.searchInput.value = initialSearchTerm;
      await this.search(initialSearchTerm);
    },
  },
  mounted() {
    this.initializeSearch();
  },
  i18n: { messages },
  directives: {
    debounce: vueDebounce({}),
  },
};
</script>

<style lang="scss" scoped>

.title {
  font-size: 90%;
}
</style>
